<template>
  <div class="h-screen flex flex-col bg-gray-50 w-full overflow-hidden">
    <div class="flex flex-1 overflow-hidden">
      <main class="flex-1 p-6 overflow-y-auto bg-gray-50">
        <div class="max-w-7xl mx-auto">

          <el-tabs v-model="activeTab" class="mb-4">
            <el-tab-pane label="影像事件紀錄" name="cctv-event"></el-tab-pane>
          </el-tabs>

          <el-card shadow="never" class="mb-4">
            <el-form :inline="true" size="default" class="flex flex-wrap gap-4">
              <el-form-item label="監視區段：" class="mb-0">
                <el-select v-model="filters.section" placeholder="全部" style="width: 200px">
                  <el-option label="全部" value="all"></el-option>
                  <el-option 
                    v-for="station in stations" 
                    :key="station" 
                    :label="station" 
                    :value="station">
                  </el-option>
                </el-select>
              </el-form-item>
              <el-form-item label="事件類型：" class="mb-0">
                <el-select v-model="filters.type" placeholder="全部" style="width: 150px">
                  <el-option label="全部" value="all"></el-option>
                  <el-option label="邊坡坍滑" value="landslide"></el-option>
                  <el-option label="動物外物" value="animal"></el-option>
                  <el-option label="火災" value="fire"></el-option>
                  <el-option label="積水" value="water"></el-option>
                  <el-option label="人員入侵" value="person"></el-option>
                </el-select>
              </el-form-item>
              <el-form-item label="查詢時間：" class="mb-0 mr-auto">
                <el-date-picker v-model="filters.dateRange" type="daterange" range-separator="-" start-placeholder="開始日期" end-placeholder="結束日期" style="width: 260px"></el-date-picker>
              </el-form-item>
            </el-form>
          </el-card>

          <el-card shadow="never" class="border-t-4 border-t-blue-500">
            <div class="flex justify-end mb-3">
              <el-input v-model="searchQuery" placeholder="搜尋CCTV..." clearable style="width: 200px; margin-right: 10px;" prefix-icon="Search" @keyup.enter.native="handleSearch" @clear="handleSearch" />
              <el-button type="primary" class="ml-2" @click="handleSearch">搜尋</el-button>
            </div>

            <el-table :data="events" v-loading="loading" style="width: 100%; margin-top: 10px;" stripe border header-cell-class-name="bg-gray-50 text-gray-700">
              <el-table-column prop="name" label="CCTV" sortable align="center" min-width="120"></el-table-column>
              <el-table-column prop="type" label="事件類型" sortable align="center" min-width="100">
                <template #default="scope">
                  <el-tag :type="getTagType(scope.row.type)" effect="light" round size="small">
                    {{ scope.row.type }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="score" label="信心值" sortable align="center" min-width="90"></el-table-column>
              <el-table-column prop="time" label="發生時間" sortable align="center" min-width="160"></el-table-column>
              <el-table-column prop="compare" label="對照時間" sortable align="center" min-width="160"></el-table-column>

              <el-table-column label="影像" align="center" min-width="90">
                <template #default="scope">
                  <el-button type="primary" size="small" plain @click="openPopup(scope.row)">查看</el-button>
                </template>
              </el-table-column>
            </el-table>

            <div class="flex justify-start mt-4" style="margin-top: 10px;">
              <el-pagination background layout="prev, pager, next" :total="total" :page-size="pageSize" :current-page.sync="currentPage" @current-change="handlePageChange"></el-pagination>
            </div>
          </el-card>
        </div>
      </main>
    </div>

    <CctvImagePopup v-if="popupCamera" :camera="popupCamera" @close="popupCamera = null" />
  </div>
</template>

<script>
import CctvImagePopup from '@/components/monitor/CctvImagePopup.vue';

export default {
  components: {
    CctvImagePopup,
  },
  data() {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);

    return {
      activeTab: 'cctv-event',
      popupCamera: null,
      searchQuery: '',
      currentPage: 1,
      pageSize: 10,
      total: 0,
      loading: false,
      filters: { section: 'all', type: 'all', dateRange: [start, end] },
      events: [],
      stations: [],
    };
  },
  watch: {
    filters: {
      handler() {
        this.currentPage = 1;
        this.fetchEvents();
      },
      deep: true,
    },
  },
  mounted() {
    this.fetchStations();
    this.fetchEvents();
  },
  methods: {
    formatDate(dateObj) {
      if (!dateObj) return '';
      const d = new Date(dateObj);
      const pad = (n) => (n < 10 ? '0' + n : n);
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    },

    async fetchStations() {
      try {
        const response = await fetch('https://thsrcctv.com/cctv_image', {
          headers: {
            'Authorization': localStorage.getItem('jwt'),
          },
        });
        const json = await response.json();

        if (json.status === 'success' && json.data) {
          const uniqueStations = [...new Set(json.data.map(item => item.Station).filter(Boolean))];
          this.stations = uniqueStations;
        }
      } catch (error) {
        console.error('取得監視區段清單失敗:', error);
      }
    },

    async fetchEvents() {
      this.loading = true;
      try {
        const url = new URL('https://thsrcctv.com/events');
        url.searchParams.append('page', this.currentPage);
        url.searchParams.append('page_size', this.pageSize);

        if (this.filters.section && this.filters.section !== 'all') {
          url.searchParams.append('section_name', this.filters.section);
        }
        if (this.filters.type && this.filters.type !== 'all') {
          url.searchParams.append('event_name', this.filters.type);
        }
        if (this.filters.dateRange && this.filters.dateRange.length === 2) {
          url.searchParams.append('start', this.formatDate(this.filters.dateRange[0]));
          url.searchParams.append('end', this.formatDate(this.filters.dateRange[1]));
        }
        if (this.searchQuery) {
          url.searchParams.append('cctv_id', this.searchQuery);
        }

        const response = await fetch(url.toString(), {
          headers: {
            'Authorization': localStorage.getItem('jwt'),
          },
        });
        const json = await response.json();

        if (json.success && json.data) {
          this.events = json.data.items.map(item => ({
            id: item.event_uuid,
            name: item.cctv_id,
            section: item.section_name,
            type: item.event_name_ch,
            eventType: item.event_name,
            score: (item.confidence * 100).toFixed(1) + '%',
            time: item.t1,
            compare: item.t0,
            status: '待處理',
            eventRef: item.event_uuid,
            description: `偵測到 ${item.event_name_ch}`,
            bboxes: [],
            polygons: [],
          }));
          this.total = json.data.total;
        } else {
          this.$message.error(json.message || '取得事件列表失敗');
        }
      } catch (error) {
        console.error('API Error:', error);
        this.$message.error('無法連接到伺服器取得事件清單');
      } finally {
        this.loading = false;
      }
    },

    handleSearch() {
      this.currentPage = 1;
      this.fetchEvents();
    },

    handlePageChange(page) {
      this.currentPage = page;
      this.fetchEvents();
    },

    async openPopup(ev) {
      this.popupCamera = { ...ev, description: '載入詳細資訊中...', bboxes: [], polygons: [] };
      
      try {
        const response = await fetch('https://thsrcctv.com/events/detail', {
          method: 'POST',
          headers: {
            'Authorization': localStorage.getItem('jwt'),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ event_ref: ev.id }),
        });
        const json = await response.json();
        
        if (json.success && json.data) {
          const detail = json.data;
          
          this.popupCamera = {
            ...this.popupCamera,
            description: detail.description || `系統提示：${detail.event_name_ch || ev.type}`,
            type: detail.event_name_ch || this.popupCamera.type,
            time: detail.t1 || this.popupCamera.time,
            compare: detail.t0 || this.popupCamera.compare,
            bboxes: detail.event_bbox?.coordinates || [],
            polygons: detail.event_polygon?.coordinates || [],
          };

          // 根據 API 文件：不使用 detail.image_url（舊版路徑）
          // 以明細回傳的 event_uuid 更新 eventRef
          if (detail.event_uuid) {
            this.popupCamera.eventRef = detail.event_uuid;
          }
        } else {
          this.popupCamera.description = `無法取得進階說明 (代碼: ${json.code})`;
        }
      } catch (error) {
        console.error('Fetch event detail failed', error);
        this.popupCamera.description = '詳細資訊連線異常';
      }
    },

    getTagType(type) {
      if (type.includes('邊坡')) return 'danger';
      if (type.includes('火災')) return 'warning';
      if (type.includes('動物') || type.includes('外物')) return 'success';
      if (type.includes('人員')) return 'primary';
      return 'info';
    },
  },
};
</script>

<style scoped>
:deep(.el-table th.el-table__cell) { background-color: #f9fafb !important; color: #374151; font-weight: 600; }
</style>